import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, User, MapPin, Globe, Plus, X, Zap, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    email: '', password: '', name: '', username: '',
    headline: '', bio: '', city: '', country: '', languages: [], referral_code: '',
  });
  const [langInput, setLangInput] = useState('');

  const handleSignup = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        email: form.email,
        password: form.password,
        name: form.name,
        username: form.username || undefined,
        bio: form.bio || undefined,
        headline: form.headline || undefined,
        city: form.city || undefined,
        country: form.country || undefined,
        languages: form.languages.length > 0 ? form.languages : undefined,
      };
      const response = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        navigate('/app');
      } else {
        setError(data.detail || 'Signup failed');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const addLanguage = () => {
    if (langInput.trim() && !form.languages.includes(langInput.trim())) {
      setForm({ ...form, languages: [...form.languages, langInput.trim()] });
      setLangInput('');
    }
  };

  const canProceed = () => {
    if (step === 1) return form.email && form.password && form.name;
    return true;
  };

  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black text-white flex" data-testid="signup-page">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-5/12 bg-gradient-to-br from-zinc-900 to-black p-10 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center space-x-3 cursor-pointer mb-12" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-orange-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-extrabold text-lg">C</span>
            </div>
            <span className="font-extrabold text-2xl text-white tracking-tight">Connect</span>
          </div>
          <h1 className="text-4xl font-extrabold text-white leading-tight mb-4">
            Your knowledge,<br />your network,<br />your profile.
          </h1>
          <p className="text-white/50 text-base leading-relaxed max-w-sm">
            Build your expertise tree, share insights, connect with people who complement your skills, and monetize your knowledge.
          </p>
        </div>
        <div className="relative z-10 flex items-center space-x-6 text-white/30 text-sm">
          <span className="flex items-center"><CheckCircle className="w-4 h-4 mr-1.5" />Verified Expertise</span>
          <span className="flex items-center"><Zap className="w-4 h-4 mr-1.5" />AI-Powered Discovery</span>
        </div>
        <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-purple-500/5" />
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-orange-500/5" />
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-lg">
          {/* Progress */}
          <div className="flex items-center mb-8">
            {[1, 2].map((s, i) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${s <= step ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-500'}`}>{s}</div>
                {i < 1 && <div className={`flex-1 h-0.5 mx-2 transition-all ${step > 1 ? 'bg-white' : 'bg-zinc-800'}`} />}
              </React.Fragment>
            ))}
          </div>

          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-xl p-3 mb-4" data-testid="signup-error">{error}</div>}

          {/* Step 1: Credentials */}
          {step === 1 && (
            <div className="animate-fadeIn" data-testid="step-1">
              <h2 className="text-2xl font-bold text-white mb-1">Create your knowledge profile</h2>
              <p className="text-sm text-zinc-500 mb-6">Everyone is both a learner and a teacher</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Full Name</label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="John Smith" data-testid="name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Username</label>
                  <input type="text" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="johnsmith" data-testid="username-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="you@email.com" data-testid="email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Password</label>
                  <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="Min 6 characters" data-testid="password-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">Referral Code (optional)</label>
                  <input type="text" value={form.referral_code} onChange={e => setForm({ ...form, referral_code: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="Enter code if you have one" data-testid="referral-code-input" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Profile Details */}
          {step === 2 && (
            <div className="animate-fadeIn" data-testid="step-2">
              <h2 className="text-2xl font-bold text-white mb-1">Tell us about yourself</h2>
              <p className="text-sm text-zinc-500 mb-6">You can always update this later</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Headline</label>
                  <input type="text" value={form.headline} onChange={e => setForm({ ...form, headline: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="e.g., Full Stack Developer & AI Enthusiast" data-testid="headline-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Bio</label>
                  <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors min-h-[80px]" placeholder="What do you know? What do you want to learn?" data-testid="bio-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-zinc-300 mb-1">City</label>
                    <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="San Francisco" data-testid="city-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-300 mb-1">Country</label>
                    <input type="text" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="United States" data-testid="country-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-zinc-300 mb-1">Languages</label>
                  <div className="flex gap-2">
                    <input type="text" value={langInput} onChange={e => setLangInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLanguage(); } }}
                      className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/30 transition-colors" placeholder="Type and press Enter" />
                    <button type="button" onClick={addLanguage} className="bg-zinc-800 text-white rounded-xl px-3 hover:bg-zinc-700 transition-colors"><Plus className="w-4 h-4" /></button>
                  </div>
                  {form.languages.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">{form.languages.map(l => (
                      <span key={l} className="inline-flex items-center bg-zinc-800 text-zinc-300 text-xs px-2.5 py-1 rounded-full">{l}
                        <button onClick={() => setForm({ ...form, languages: form.languages.filter(x => x !== l) })} className="ml-1 hover:text-red-400"><X className="w-3 h-3" /></button>
                      </span>
                    ))}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center space-x-1 text-sm font-semibold text-zinc-500 hover:text-white transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /><span>Back</span>
              </button>
            ) : (
              <button onClick={() => navigate('/login')} className="text-sm text-zinc-500 hover:text-white transition-colors" data-testid="login-link">
                Already have an account? <span className="font-semibold">Log In</span>
              </button>
            )}
            {step < 2 ? (
              <button onClick={() => canProceed() && setStep(step + 1)} disabled={!canProceed()}
                className="bg-white text-black font-bold rounded-full px-6 py-2.5 text-sm flex items-center space-x-2 disabled:opacity-30 hover:bg-zinc-200 transition-colors" data-testid="next-btn">
                <span>Next</span><ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleSignup} disabled={loading}
                className="bg-white text-black font-bold rounded-full px-6 py-2.5 text-sm flex items-center space-x-2 disabled:opacity-30 hover:bg-zinc-200 transition-colors" data-testid="signup-btn">
                <span>{loading ? 'Creating...' : 'Create Profile'}</span>{!loading && <Zap className="w-4 h-4" />}
              </button>
            )}
          </div>

          {/* Google Auth */}
          <div className="mt-6">
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-black text-zinc-600">or</span></div>
            </div>
            <button onClick={handleGoogleSignup}
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-full font-semibold px-6 py-2.5 text-sm transition-colors hover:bg-zinc-800 flex items-center justify-center space-x-2"
              data-testid="google-signup-button">
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
